import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getMintSpaceById, getMintSpaceRating, joinMintSpace } from "../api/mintSpace";
import { getBusinessCategoryShortLabel } from "../constants/businessCategories";
import { useTranslation } from "../context/TranslationContext";
import { getMintSpaceAppStoreCatalog } from "../data/mintSpaceAppStoreCatalog";
import { getMintSpaceJoinDisplay, hasMintSpaceApp } from "../utils/mintSpaceApps";
import { formatRating } from "./discover/campaignDisplay";
import Avatar from "./ds/Avatar";
import Button from "./ds/Button";
import Icon from "./ds/Icon";
import JoinRequestModal from "./JoinRequestModal";

/*
  Figma "Space gate" (B20): the card someone sees before joining a space, as a popup over
  whatever page they were on, with that page blurred behind it. The card sits on the space's
  own banner, or its logo without one, so it stays dark in the light theme too.
*/

const FADE =
  "linear-gradient(to bottom, rgba(10,10,13,.35) 0%, rgba(10,10,13,.05) 30%, rgba(10,10,13,.55) 55%, rgba(10,10,13,.9) 75%, rgba(10,10,13,.97) 100%)";

const Chip = ({ children }) => (
  <span className="flex h-[42px] items-center gap-space-8 rounded-full border border-separator-default bg-material-thin pl-space-12 pr-space-16 text-footnote-semibold text-label-primary backdrop-blur-[24px]">
    {children}
  </span>
);

const SpaceGate = ({ space, setToast, isDark, onClose, onJoined }) => {
  const { t, language } = useTranslation();
  const [rating, setRating] = useState(null);
  const [joining, setJoining] = useState(false);
  const [pending, setPending] = useState(!!space.hasPendingRequest);
  const [askingQuestions, setAskingQuestions] = useState(false);
  const panelRef = useRef(null);
  // The questions popup sits above this one, and Esc closes that one first.
  const closeRef = useRef(onClose);
  closeRef.current = askingQuestions ? null : onClose;

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeRef.current?.();
    };
    document.addEventListener("keydown", onKeyDown);
    panelRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      previousFocus?.focus?.();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getMintSpaceRating(space._id)
      .then((result) => !cancelled && result.success && setRating(result.data))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [space._id]);

  // Each kind of app once, by its own name: two Live Streams are one chip.
  const apps = useMemo(() => getMintSpaceAppStoreCatalog(t).filter((app) => hasMintSpaceApp(space, app.id)), [space, t]);

  const cover = space.pages?.welcome?.banner || space.logo;
  const price = getMintSpaceJoinDisplay(space);
  const showPrice = space.pages?.pricing?.enabled !== false;
  const hasQuestions = space.questions?.length > 0;
  const reviews = rating?.ratingCount
    ? `${formatRating(rating.averageRating, language)} · ${t(rating.ratingCount === 1 ? "home.reviewsCountOne" : "home.reviewsCount", {
        count: rating.ratingCount,
      })}`
    : t("mintSpace.noReviewsYet");
  const meta = [reviews, space.category && getBusinessCategoryShortLabel(t, space.category)].filter(Boolean).join(" · ");

  const join = async () => {
    setJoining(true);
    try {
      const result = await joinMintSpace(space._id);
      if (result.success) {
        setToast(t("mintSpace.successfullyJoined"));
        window.dispatchEvent(new CustomEvent("mintSpaceJoined", { detail: { mintSpaceId: space._id } }));
        const fresh = await getMintSpaceById(space._id);
        onJoined(fresh.data);
      }
    } catch (error) {
      setToast(error?.message || t("mintSpace.errorJoiningMintSpace"));
    } finally {
      setJoining(false);
    }
  };

  return createPortal(
    <div data-theme="dark" className="fixed inset-0 z-[550] flex items-center justify-center p-space-16 font-ds text-label-primary">
      <div className="absolute inset-0 bg-overlay-scrim backdrop-blur-[16px] animate-ds-fade-in" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={space.name}
        tabIndex={-1}
        className="relative flex h-[580px] max-h-full w-full flex-col justify-between gap-space-16 overflow-hidden rounded-radius-xl bg-bg-raised p-space-20 outline-none animate-ds-pop-in min-[600px]:h-[620px] min-[600px]:w-[520px] min-[600px]:p-space-24 lg:h-[640px] lg:w-[560px]"
      >
        {cover && <img src={cover} alt="" className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0" style={{ backgroundImage: FADE }} aria-hidden="true" />

        <div className="relative flex flex-wrap items-center justify-between gap-space-8">
          <Chip>
            <Icon name="lock" size={24} />
            {t("mintSpace.membersOnly")}
          </Chip>
          {showPrice && (
            <Chip>
              <Icon name={price.isFree ? "check" : "wallet"} size={24} />
              {price.isFree ? t("mintSpace.freeToJoin") : `${price.amount} ${price.currency === "GEL" ? "₾" : price.currency}`}
            </Chip>
          )}
        </div>

        <div className="relative flex min-h-0 flex-col gap-space-20">
          <div className="flex shrink-0 items-center gap-space-12">
            <Avatar size="l" src={space.logo} name={space.name} />
            <div className="flex min-w-0 flex-1 flex-col gap-space-4">
              <h2 className="truncate text-title-2 text-label-primary min-[600px]:text-title-1">{space.name}</h2>
              <p className="truncate text-footnote text-label-secondary">{meta}</p>
            </div>
          </div>

          {space.bio && <p className="line-clamp-3 shrink-0 text-subheadline text-label-secondary">{space.bio}</p>}

          {apps.length > 0 && (
            // Takes what room the card has left, and scrolls past that.
            <div className="flex min-h-0 flex-wrap gap-space-8 overflow-y-auto">
              {apps.map(({ id, name, icon: AppIcon }) => (
                <Chip key={id}>
                  <AppIcon className="size-6" />
                  {name}
                </Chip>
              ))}
            </div>
          )}

          <Button
            variant="gray"
            fullWidth
            icon={pending ? null : <Icon name="arrow-right" size={20} />}
            loading={joining}
            disabled={pending}
            onClick={hasQuestions ? () => setAskingQuestions(true) : join}
            className={`max-[599px]:h-button-m max-[599px]:text-caps-button-m ${pending ? "" : "!bg-message-info-bg !text-accent-default"}`}
          >
            {pending ? t("mintSpace.requestSent") : hasQuestions ? t("mintSpace.requestToJoin") : t("home.join")}
          </Button>
        </div>
      </div>

      {askingQuestions && (
        <JoinRequestModal
          isOpen
          setIsOpen={setAskingQuestions}
          mintSpace={space}
          isDark={isDark}
          setToast={setToast}
          onSuccess={() => setPending(true)}
        />
      )}
    </div>,
    document.body
  );
};

export default SpaceGate;
